import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import SEO from '@/components/SEO';

const TermsPage = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Terms of Service',
      lastUpdated: 'Last Updated: January 2024',
      sections: [
        {
          title: 'Acceptance of Terms',
          content: 'By accessing and using The Grass Depot LLC website and services, you accept and agree to be bound by the terms and provision of this agreement.'
        },
        {
          title: 'Services',
          content: 'The Grass Depot LLC provides landscaping materials including grass, sod, rock, mulch, soil, and sand, along with delivery services throughout Montgomery County, Texas. All quotes are estimates and final pricing may vary based on availability and delivery conditions.'
        },
        {
          title: 'Orders and Delivery',
          content: 'All orders are subject to availability. Delivery times are estimates and may vary due to weather conditions, traffic, or other unforeseen circumstances. We will make every effort to deliver your order on the scheduled date.'
        },
        {
          title: 'Payment Terms',
          content: 'Payment is due at the time of delivery unless otherwise arranged. We accept cash, check, and major credit cards. For large orders, we may require a deposit.'
        },
        {
          title: 'Cancellations and Returns',
          content: 'Orders can be cancelled up to 24 hours before the scheduled delivery time. Due to the perishable nature of sod and the bulk nature of our materials, returns are handled on a case-by-case basis.'
        },
        {
          title: 'Limitation of Liability',
          content: 'The Grass Depot LLC is not responsible for damages to property during delivery if adequate access is not provided. Customers are responsible for ensuring safe and accessible delivery locations.'
        },
        {
          title: 'Warranty',
          content: 'We guarantee the quality of our products at the time of delivery. Any concerns about product quality must be reported within 24 hours of delivery.'
        },
        {
          title: 'Changes to Terms',
          content: 'We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to our website.'
        },
        {
          title: 'Contact Information',
          content: 'For questions about these Terms of Service, please contact us at:\nThe Grass Depot LLC\n6920 FM 1488 RD, Magnolia, TX 77354\nPhone: (936) 320-0763\nEmail: thegrassdepotllc@gmail.com'
        }
      ]
    },
    es: {
      title: 'Términos de Servicio',
      lastUpdated: 'Última Actualización: Enero 2024',
      sections: [
        {
          title: 'Aceptación de Términos',
          content: 'Al acceder y utilizar el sitio web y los servicios de The Grass Depot LLC, acepta y acuerda estar sujeto a los términos y disposiciones de este acuerdo.'
        },
        {
          title: 'Servicios',
          content: 'The Grass Depot LLC proporciona materiales de jardinería que incluyen césped, pasto, roca, mulch, tierra y arena, junto con servicios de entrega en todo el Condado de Montgomery, Texas. Todas las cotizaciones son estimaciones y el precio final puede variar según la disponibilidad y las condiciones de entrega.'
        },
        {
          title: 'Pedidos y Entrega',
          content: 'Todos los pedidos están sujetos a disponibilidad. Los tiempos de entrega son estimaciones y pueden variar debido a condiciones climáticas, tráfico u otras circunstancias imprevistas. Haremos todo lo posible para entregar su pedido en la fecha programada.'
        },
        {
          title: 'Términos de Pago',
          content: 'El pago vence en el momento de la entrega a menos que se acuerde lo contrario. Aceptamos efectivo, cheques y las principales tarjetas de crédito. Para pedidos grandes, podemos requerir un depósito.'
        },
        {
          title: 'Cancelaciones y Devoluciones',
          content: 'Los pedidos se pueden cancelar hasta 24 horas antes de la hora de entrega programada. Debido a la naturaleza perecedera del césped y la naturaleza a granel de nuestros materiales, las devoluciones se manejan caso por caso.'
        },
        {
          title: 'Limitación de Responsabilidad',
          content: 'The Grass Depot LLC no es responsable de daños a la propiedad durante la entrega si no se proporciona acceso adecuado. Los clientes son responsables de garantizar ubicaciones de entrega seguras y accesibles.'
        },
        {
          title: 'Garantía',
          content: 'Garantizamos la calidad de nuestros productos en el momento de la entrega. Cualquier inquietud sobre la calidad del producto debe informarse dentro de las 24 horas posteriores a la entrega.'
        },
        {
          title: 'Cambios a los Términos',
          content: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigencia inmediatamente después de su publicación en nuestro sitio web.'
        },
        {
          title: 'Información de Contacto',
          content: 'Para preguntas sobre estos Términos de Servicio, contáctenos en:\nThe Grass Depot LLC\n6920 FM 1488 RD, Magnolia, TX 77354\nTeléfono: (936) 320-0763\nEmail: thegrassdepotllc@gmail.com'
        }
      ]
    }
  };

  const currentContent = content[language as keyof typeof content];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={currentContent.title}
        description={language === 'en' 
          ? 'Terms of Service for The Grass Depot LLC - Understand our policies for orders, delivery, and services.'
          : 'Términos de Servicio de The Grass Depot LLC - Comprenda nuestras políticas para pedidos, entrega y servicios.'}
        canonical={`/${language}/terms`}
      />
      <Header />
      
      <main className="flex-grow bg-background">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {currentContent.title}
          </h1>
          <p className="text-muted-foreground mb-12">{currentContent.lastUpdated}</p>

          <div className="space-y-8">
            {currentContent.sections.map((section, index) => (
              <section key={index} className="bg-card p-6 rounded-lg border border-border shadow-sm">
                <h2 className="text-2xl font-semibold text-primary mb-4">
                  {section.title}
                </h2>
                <p className="text-foreground whitespace-pre-line leading-relaxed">
                  {section.content}
                </p>
              </section>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      <MobileCTA />
    </div>
  );
};

export default TermsPage;
